import React, { useEffect, useMemo, useState } from "react";
import type { MediaView } from "./mediaTypes";
import { HKPImage } from "./HKPImage";
import { safeDeliveryUrl } from "./mediaPresets";
import { fetchLegacyCopies, fullViewMedia, isStoredS3Photo, slowConnection, softPreviewUrl, viewerSizes } from "./fullViewMedia";

type Props = {
  item: any;
  src: string;
  alt?: string;
  /** The user zoomed in: fetch the 2000 px copy even on a slow link. */
  zoomed?: boolean;
  onLoad?: React.ReactEventHandler<HTMLImageElement>;
  /** Called once the photo cannot be shown at all (not when a copy fails and the stored link is tried). */
  onError?: React.ReactEventHandler<HTMLImageElement>;
};

/**
 * A photo opened full screen. The browser picks the copy from the drawn width and pixel density (a 2x
 * laptop gets 2000 px, a phone 1200 px); a slow link stops at 1200 px until the user zooms. One image
 * element throughout: it starts on the small copy and is upgraded in place, and the browser keeps
 * showing the small one until the sharp one has loaded, so the screen is never blank.
 */
export const FullViewImage = React.forwardRef<HTMLImageElement, Props>(function FullViewImage(
  { item, src, alt = "", zoomed, onLoad, onError }, ref
) {
  const known = useMemo(() => fullViewMedia(item), [item]);
  const [looked, setLooked] = useState<MediaView | undefined>();
  const [soft, setSoft] = useState(() => Boolean(softPreviewUrl(known)));
  const [softFailed, setSoftFailed] = useState(false);
  useEffect(() => {
    setSoft(Boolean(softPreviewUrl(known)));
    setSoftFailed(false);
    setLooked(undefined);
    if (known || !isStoredS3Photo(src)) return undefined;
    let live = true;
    void fetchLegacyCopies(src).then((view) => { if (live) setLooked(view); });
    return () => { live = false; };
  }, [known, src]);
  const media = known || looked;
  const preset = soft ? "THUMBNAIL" : zoomed || !slowConnection() ? "ZOOM" : "PRODUCT_DETAIL";
  const legacy = src.startsWith("blob:") ? undefined : src;
  return (
    <HKPImage
      // A failed small copy would leave HKPImage on the stored link; a fresh element goes to the sharp copy.
      key={softFailed ? "sharp" : "soft-then-sharp"}
      ref={ref}
      preset={preset}
      media={media}
      mediaId={media || typeof item !== "object" ? undefined : item?.mediaId || undefined}
      legacyUrl={legacy}
      localPreviewUrl={src.startsWith("blob:") ? src : undefined}
      sizes={viewerSizes(media, zoomed)}
      loading="eager"
      alt={alt}
      onLoad={(event) => {
        setSoft(false);
        onLoad?.(event);
      }}
      onError={(event) => {
        if (soft) { setSoft(false); setSoftFailed(true); return; }
        // HKPImage retries the stored link after a failed copy; only that last failure is the caller's.
        const stored = safeDeliveryUrl(legacy);
        if (stored && event.currentTarget.getAttribute("src") !== stored) return;
        onError?.(event);
      }}
    />
  );
});
